import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { format, subDays } from "date-fns";
import { useAuth } from "@/lib/auth";
import { JobCategory } from "@/lib/types/job";
import { analytics, ANALYTICS_EVENTS } from "@/lib/analytics";

// Components
import { DateRangeSelector } from "@/components/reports/spend/DateRangeSelector";
import { CategoryMultiSelect } from "@/components/reports/vendors/CategoryMultiSelect";
import { CycleTimeKPICards } from "@/components/reports/cycle-times/CycleTimeKPICards";
import { WorkflowStageBreakdown } from "@/components/reports/cycle-times/WorkflowStageBreakdown";
import { CycleTimeByCategory } from "@/components/reports/cycle-times/CycleTimeByCategory";
import { CycleTimeTrendChart } from "@/components/reports/cycle-times/CycleTimeTrendChart";
import { CycleTimeDistributionChart } from "@/components/reports/cycle-times/CycleTimeDistributionChart";
import { CycleTimeByRequesterTable } from "@/components/reports/cycle-times/CycleTimeByRequesterTable";
import { ProductionTimeByVendorTable } from "@/components/reports/cycle-times/ProductionTimeByVendorTable";
import { CycleTimeExportButton } from "@/components/reports/cycle-times/CycleTimeExportButton";

// Hooks and utilities
import { 
  useCycleTimeData, 
  usePreviousPeriodData,
  computeKPIs,
  computeStageBreakdown,
  computeCategoryBreakdown,
  computeTrend,
  computeRequesterBreakdown,
  computeVendorBreakdown,
  computeDistribution,
} from "@/hooks/useCycleTimeAnalytics";

export default function CycleTimesReportPage() {
  const { user, organization } = useAuth();
  const organizationId = organization?.id;
  
  // Date range state - default to last 90 days
  const [startDate, setStartDate] = useState<Date>(() => subDays(new Date(), 90));
  const [endDate, setEndDate] = useState<Date>(new Date());
  
  // Category filter state
  const [selectedCategories, setSelectedCategories] = useState<JobCategory[]>([]);

  // Check if user is admin (can see all requesters)
  const isAdmin = user?.role === 'admin' || user?.orgRole === 'owner' || user?.orgRole === 'super_user';

  // Track report viewed
  useEffect(() => {
    analytics.track({
      name: ANALYTICS_EVENTS.REPORT_VIEWED,
      category: 'engagement',
      properties: {
        report_type: 'cycle_times',
      },
    });
  }, []);

  // Fetch data
  const { data: currentData, isLoading: isLoadingCurrent } = useCycleTimeData(
    startDate,
    endDate,
    organizationId,
    selectedCategories
  );

  const { data: previousData, isLoading: isLoadingPrevious } = usePreviousPeriodData(
    startDate,
    endDate,
    organizationId,
    selectedCategories
  );

  const isLoading = isLoadingCurrent || isLoadingPrevious;

  // Compute derived data
  const kpis = useMemo(() => {
    if (!currentData || !previousData) return null;
    return computeKPIs(currentData, previousData, startDate, endDate);
  }, [currentData, previousData, startDate, endDate]);

  const stageBreakdown = useMemo(() => {
    return computeStageBreakdown(currentData || []);
  }, [currentData]);

  const categoryBreakdown = useMemo(() => {
    return computeCategoryBreakdown(currentData || []);
  }, [currentData]);

  const trendData = useMemo(() => {
    return computeTrend(currentData || [], startDate, endDate);
  }, [currentData, startDate, endDate]);

  const requesterBreakdown = useMemo(() => {
    return computeRequesterBreakdown(currentData || []);
  }, [currentData]);

  const vendorBreakdown = useMemo(() => {
    return computeVendorBreakdown(currentData || []);
  }, [currentData]);

  const distributionData = useMemo(() => {
    if (!currentData || currentData.length === 0) return null;
    return computeDistribution(currentData);
  }, [currentData]);

  // Handle date range changes
  const handleDateChange = (start: Date, end: Date) => {
    setStartDate(start);
    setEndDate(end);
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/reports">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Cycle Time Analytics</h1>
            <p className="text-muted-foreground mt-1">
              Track procurement cycle times and identify efficiency opportunities
            </p>
          </div>
        </div>
        <CycleTimeExportButton 
          data={currentData || []} 
          startDate={startDate} 
          endDate={endDate}
          isLoading={isLoading}
        />
      </div>


      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <DateRangeSelector
          startDate={startDate}
          endDate={endDate}
          onDateChange={handleDateChange}
        />
        <CategoryMultiSelect
          selectedCategories={selectedCategories}
          onChange={setSelectedCategories}
        />
      </div>

      {/* KPI Cards */}
      <CycleTimeKPICards kpis={kpis} isLoading={isLoading} />

      {/* Workflow Stage Breakdown */}
      <WorkflowStageBreakdown breakdown={stageBreakdown} isLoading={isLoading} />

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        <CycleTimeByCategory data={categoryBreakdown} isLoading={isLoading} />
        <CycleTimeTrendChart data={trendData} isLoading={isLoading} />
      </div>

      {/* Distribution */}
      <CycleTimeDistributionChart data={distributionData} isLoading={isLoading} />

      {/* Tables Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        <CycleTimeByRequesterTable 
          data={requesterBreakdown} 
          isLoading={isLoading}
          isAdmin={isAdmin}
          currentUserId={user?.id}
        />
        <ProductionTimeByVendorTable 
          data={vendorBreakdown} 
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
